import React, { useCallback, useMemo } from "react";
import * as Yup from "yup";
import {
  WSActions,
  WSCard,
  WSForm,
  WSInfoBox,
  WSInputTextarea,
  WSList,
  WSSectionToolbar,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  EDD_FIELDS,
  EDDQuestionnaireFormData,
  ProhibitedJurisdictionExposure
} from "./utils";
import { updateCustomerData } from "../../../../services/api/onboarding/customer";
import { useQueryVerificationMissingData } from "../../../../query/onboarding/queries/useQueryVerificationMissingFields";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { performVerification } from "../../../../services/api/onboarding/verifications";
import { useQueryVerifications } from "../../../../query/onboarding/queries/useQueryVerifications";

interface EDDQuestionnaireProps {
  onCancel?: () => void;
  onSubmit?: (data: EDDQuestionnaireFormData) => void;
  isLoading?: boolean;
}

export const EDDQuestionnaire: React.FC<EDDQuestionnaireProps> = ({
  onCancel,
  onSubmit,
  isLoading = false
}) => {
  const queryMissingDataEnhanced = useQueryVerificationMissingData("Enhanced");
  const queryVerifications = useQueryVerifications();

  const handleSubmit = useCallback(
    async (data: EDDQuestionnaireFormData) => {
      const { prohibitedJurisdictionExposureDetails, ...customerData } = data;
      const requestCustomerData: Record<string, unknown> = { ...customerData };

      if (
        customerData.prohibitedJurisdictionExposure ===
        ProhibitedJurisdictionExposure.Yes
      ) {
        requestCustomerData.prohibitedJurisdictionExposureDetails =
          prohibitedJurisdictionExposureDetails?.trim();
      }

      await updateCustomerData("Entity", {
        customerData: requestCustomerData
      });

      await performVerification("Enhanced");

      await queryVerifications.refetch();
      await queryMissingDataEnhanced.refetch();

      onSubmit?.(data);
    },
    [onSubmit]
  );

  const handleCancel = useCallback(async () => {
    onCancel?.();
  }, [onCancel]);

  const validationSchema = useMemo(() => {
    let shape = {};

    EDD_FIELDS.filter(field =>
      queryMissingDataEnhanced.data?.requiredFields?.includes(field.name)
    ).forEach(field => {
      const fieldSchema =
        field.componentProps.required !== false
          ? Yup.string().required("This field is required")
          : Yup.string();

      shape = {
        ...shape,
        [field.name]: fieldSchema
      };

      if (field.name === "prohibitedJurisdictionExposure") {
        shape = {
          ...shape,
          prohibitedJurisdictionExposureDetails: Yup.string().when(
            "prohibitedJurisdictionExposure",
            {
              is: ProhibitedJurisdictionExposure.Yes,
              then: Yup.string().trim().required("This field is required"),
              otherwise: Yup.string()
            }
          )
        };
      }
    });

    return Yup.object().shape(shape);
  }, [queryMissingDataEnhanced.data?.requiredFields]);

  const defaultValues = useMemo(() => {
    let defaultValues = {};

    EDD_FIELDS.filter(field =>
      queryMissingDataEnhanced.data?.requiredFields?.includes(field.name)
    ).forEach(field => {
      defaultValues = {
        ...defaultValues,
        [field.name]: ""
      };

      if (field.name === "prohibitedJurisdictionExposure") {
        defaultValues = {
          ...defaultValues,
          prohibitedJurisdictionExposureDetails: ""
        };
      }
    });

    return defaultValues;
  }, [queryMissingDataEnhanced.data?.requiredFields]);

  if (!queryMissingDataEnhanced.isSuccess) {
    return null;
  }

  return (
    <>
      <WSSectionToolbar title="Complete questionnaire"></WSSectionToolbar>
      <WSForm<EDDQuestionnaireFormData>
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        defaultValues={defaultValues}
      >
        <WSList gap="L">
          <WSInfoBox
            theme="neutral"
            icon="info-circle"
            title="Why am I seeing this?"
          >
            To pay contractors/invoice clients, we need some additional
            information.
          </WSInfoBox>

          {EDD_FIELDS.filter(field =>
            queryMissingDataEnhanced.data?.requiredFields?.includes(field.name)
          ).map(({ title, name, label, componentProps }, index) => (
            <WSCard key={name} header={`${index + 1}. ${title}`}>
              <WSForm.Field
                name={name}
                label={label}
                component={WSSelect}
                componentProps={{
                  placeholder: "Select an option",
                  required: componentProps.required !== false,
                  ...componentProps
                }}
              />
              {name === "prohibitedJurisdictionExposure" && (
                <WSForm.Value name="prohibitedJurisdictionExposure">
                  {prohibitedJurisdictionExposure =>
                    prohibitedJurisdictionExposure ===
                    ProhibitedJurisdictionExposure.Yes ? (
                      <WSForm.Field
                        mt="M"
                        name="prohibitedJurisdictionExposureDetails"
                        label="Please describe"
                        component={WSInputTextarea}
                        componentProps={{
                          required: true,
                          placeholder:
                            "Describe the clients, contractors, vendors, or payments in these jurisdictions"
                        }}
                      />
                    ) : null
                  }
                </WSForm.Value>
              )}
            </WSCard>
          ))}

          <WSCard>
            <WSText.ParagraphSm>
              At Wingspan, we are required by law to verify your business. This
              helps us prevent fraud and comply with regulations. Your
              cooperation ensures a safe and secure experience for everyone.
              Thank you for providing the necessary information and documents.
            </WSText.ParagraphSm>
            <WSText.ParagraphSm mt="M">
              The review period typically takes about one (1) business day,
              during this time you may not be able to send/receive payments or
              access banking features.
            </WSText.ParagraphSm>
          </WSCard>

          <WSErrorMessage
            error={queryMissingDataEnhanced.error}
            contextKey="Other"
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                onClick: handleCancel,
                label: "Cancel",
                type: "button",
                disabled: isLoading
              },
              {
                label: "Continue",
                loading: isLoading,
                type: "submit"
              }
            ]}
          />

          <WSText.ParagraphXs color="gray400" mt="M">
            This form is meant for the recipient listed at the top of this page.
            Please disregard and notify Wingspan immediately if this is not you.
          </WSText.ParagraphXs>
        </WSList>
      </WSForm>
    </>
  );
};
